import * as yup from 'yup';

/**
 * Operator-supplied options for an Atlas-pipeline GL delivery. Mirrors the
 * query-param contract of `POST /file-upload/atlas/general-ledger`:
 *  - sourceObject: rule key matching MappingRule.sourceObject in the template.
 *    Backend default is 'gl'; this UI sends it explicitly to keep the wire
 *    contract auditable and avoid live-view-stitching drift when the operator
 *    declares a new source (e.g. 'positions', 'memo', 'trialBalanceGL').
 *  - coverageStart / coverageEnd: ISO YYYY-MM-DD calendar bounds declaring
 *    the file's coverage window. Both optional; if omitted, backend infers
 *    from the row data's gl_date min/max.
 *
 * The grid-display schema lives in `GeneralLedgerUploadData.schema.ts`.
 * Keeping the upload-options schema separate avoids muddling "what the
 * operator submits" with "what the grid renders".
 */

/** Same character class the backend enforces (`/^[A-Za-z0-9_-]+$/`). Mirror
 *  it client-side so the operator sees an inline error instead of a 400. */
export const SOURCE_OBJECT_PATTERN = /^[A-Za-z0-9_-]+$/;

export function atlasGlUploadOptionsSchema() {
  return yup.object({
    sourceObject: yup
      .string()
      .required('Source object is required')
      .matches(
        SOURCE_OBJECT_PATTERN,
        'Source object may only contain letters, numbers, dashes, and underscores'
      ),
    coverageStart: yup
      .date()
      .nullable()
      .transform((v, raw) => (raw === '' || raw == null ? null : v))
      .optional(),
    coverageEnd: yup
      .date()
      .nullable()
      .transform((v, raw) => (raw === '' || raw == null ? null : v))
      .optional()
      .test('coverage-range', 'Coverage end must be on or after coverage start', function checkRange(end) {
        const start = this.parent.coverageStart as Date | null | undefined;
        if (!start || !end) {
          return true;
        }
        return end.getTime() >= start.getTime();
      }),
  });
}

export type AtlasGlUploadOptions = yup.InferType<ReturnType<typeof atlasGlUploadOptionsSchema>>;
